import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  CalendarLine,
  MetricsLine,
  MilestoneLine,
  MinusLine,
  PaperDocLine,
  PersonLine,
  SyncingLine,
  TeamLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {LaptopMini} from '@dropbox/dig-illustrations';
import {useQuery} from '@tanstack/react-query';
import {AuditLog, TeamService, WorkstreamAuditLog, WorkstreamMetric} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {EmptyState} from 'components/DSYS/EmptyState';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {UpdatePost} from 'components/DSYS/UpdatePost';
import {LinkIcon} from 'components/shared/LinkIcon';
import {format} from 'date-fns';
import {formatMetricForecastPeriod} from 'helpers/metricForecast';
import {useEmployee} from 'hooks/useEmployee';
import {t} from 'i18next';
import {Fragment} from 'react';
import {Trans} from 'react-i18next';
import {getService} from 'utilities';
import {useWorkstream} from 'views/workstreams/hooks';
import {getI18nDayName} from 'views/workstreams/utils';

const isDeletion = (log: WorkstreamAuditLog | AuditLog) => !!log.before && !log.after;
const isAddition = (log: WorkstreamAuditLog | AuditLog) => !!log.after && !log.before;
const CHANGE_LOG_CONTENT_MAX_WIDTH = 'var(--breakpoint__medium)';

const formatMetricForecastDate = (forecastDate: string | null | undefined) => {
  if (!forecastDate) {
    return null;
  }

  const parsedDate = new Date(forecastDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return forecastDate;
  }

  return format(parsedDate, 'MMM dd, yyyy');
};

const getAuditTitleText = (title: string | null | undefined) => {
  if (!title) {
    return '';
  }

  return parseAndExtractTextFromLexicalJson(title) || title;
};

type MnpiMetricAuditPayload = {
  is_mnpi?: boolean | null;
  metric_id?: number | string | null;
  mnpi_enabled?: boolean | null;
  title?: string | null;
  user_ids?: string[] | null;
};

const parseMnpiMetricAuditPayload = (value: string): MnpiMetricAuditPayload | null => {
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as MnpiMetricAuditPayload;
  } catch {
    return null;
  }
};

const getMnpiMetricAuditTitle = (
  payload: MnpiMetricAuditPayload,
  metrics: WorkstreamMetric[] = []
) => {
  const auditTitle = getAuditTitleText(payload.title);
  if (auditTitle) {
    return auditTitle;
  }

  const metricId = payload.metric_id;
  const metric = metrics.find((item) => String(item.id) === String(metricId));
  const metricTitle = metric?.title
    ? parseAndExtractTextFromLexicalJson(metric.title) || metric.title
    : null;

  if (metricTitle) {
    return metricTitle;
  }

  if (metricId != null && metricId !== '') {
    return t('workstream_audit_log_mnpi_metric_fallback', {id: metricId});
  }

  return t('workstream_audit_log_mnpi_metric_unknown');
};

const getMnpiFlagValue = (payload: MnpiMetricAuditPayload) => {
  if (typeof payload.mnpi_enabled === 'boolean') {
    return payload.mnpi_enabled;
  }

  if (typeof payload.is_mnpi === 'boolean') {
    return payload.is_mnpi;
  }

  return null;
};

export const groupByTimeAndType = (logs: (WorkstreamAuditLog | AuditLog)[]) => {
  const grouped: {[key: string]: {[type: string]: (WorkstreamAuditLog | AuditLog)[]}} = {};

  logs.forEach((log) => {
    const date = new Date(log.date);

    const minutes = Math.floor(date.getMinutes() / 1);
    date.setMinutes(minutes, 0, 0);
    const intervalKey = date.toISOString();

    if (!grouped[intervalKey]) {
      grouped[intervalKey] = {};
    }

    if (!grouped[intervalKey][log.type]) {
      grouped[intervalKey][log.type] = [];
    }

    grouped[intervalKey][log.type].push(log);
  });

  Object.values(grouped).forEach((byType) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(byType).forEach(([_, logsForType]) => {
      logsForType.sort((a, b) => {
        // Always order "-" before "+"
        if (isDeletion(a) && isAddition(b)) {
          return -1;
        }
        if (isAddition(a) && isDeletion(b)) {
          return 1;
        }
        // If both are additions or deletions, order by timestamp
        return Date.parse(a.date) - Date.parse(b.date);
      });
    });
  });

  return grouped;
};

const FormattedChangeLog = ({
  type,
  value,
  node,
  metrics,
}: {
  type: string;
  value: string;
  node?: React.ReactNode;
  metrics?: WorkstreamMetric[];
}) => {
  if (type === 'done') {
    return <RichTextArea value={value} textColor="inherit" textSize="small" />;
  } else if (type === 'status_due') {
    return <>{getI18nDayName(parseInt(value, 10))}</>;
  } else if (type === 'end_date') {
    return (
      <Box display="flex" alignItems="center" style={{gap: 4}}>
        <UIIcon src={CalendarLine} size="small" className={atoms({color: 'Text Subtle'})} />
        <Text color="subtle">{format(new Date(value), 'MMM dd, yyyy')}</Text>
      </Box>
    );
  } else if (type === 'description') {
    return <RichTextArea value={value} textColor="subtle" textSize="small" />;
  } else if (type === 'update_deleted') {
    return node ? <>{node}</> : <RichTextArea value={value} textColor="inherit" textSize="small" />;
  } else if (type === 'mnpi_metric_toggle') {
    const payload = parseMnpiMetricAuditPayload(value);
    const mnpiFlag = payload ? getMnpiFlagValue(payload) : null;

    if (!payload || mnpiFlag == null) {
      return <>{value}</>;
    }

    return (
      <Box display="flex" flexDirection="column" style={{gap: 2}}>
        <Text isBold>{getMnpiMetricAuditTitle(payload, metrics)}</Text>
        <Text color="subtle">
          {t('workstream_audit_log_mnpi_metric_flag', {
            value: t(
              mnpiFlag
                ? 'workstream_audit_log_mnpi_metric_enabled'
                : 'workstream_audit_log_mnpi_metric_disabled'
            ),
          })}
        </Text>
      </Box>
    );
  } else if (type === 'mnpi_metric_audience') {
    const payload = parseMnpiMetricAuditPayload(value);
    const userIds = payload?.user_ids?.map(String).filter(Boolean) ?? [];

    if (!payload) {
      return <>{value}</>;
    }

    return (
      <Box style={{gap: 2}}>
        <Text isBold>{getMnpiMetricAuditTitle(payload, metrics)}</Text>
        <Box display="flex" flexWrap="wrap" alignItems="baseline" style={{columnGap: 4, rowGap: 2}}>
          <Text color="subtle">{t('workstream_audit_log_mnpi_metric_audience')}</Text>
          {userIds.length ? (
            userIds.map((userId, index) => (
              <Fragment key={`${userId}-${index}`}>
                <LoadableAudienceEmployeeName userId={userId} />
                {index < userIds.length - 1 && <Text color="subtle">,</Text>}
              </Fragment>
            ))
          ) : (
            <Text color="subtle">{t('workstream_audit_log_mnpi_metric_no_audience')}</Text>
          )}
        </Box>
      </Box>
    );
  } else if (type === 'link') {
    try {
      const {icon, text, url} = JSON.parse(value);
      return (
        <Box display="flex">
          <LinkIcon
            className={atoms({color: 'Text Subtle', flexShrink: 0})}
            size="small"
            style={{marginTop: 3}}
            icon={icon}
          />
          <Truncate tooltipControlProps={{placement: 'top'}} maxWidth={400}>
            <Text color="subtle">
              {text} ({url})
            </Text>
          </Truncate>
        </Box>
      );
    } catch {
      return <>{value}</>;
    }
  } else if (type === 'privacy') {
    try {
      const {name, type: privacyType} = JSON.parse(value);
      return (
        <Box display="flex" alignItems="center" style={{gap: 2}}>
          <Text color="subtle">{t('workstream_audit_log_custom_privacy_audience')}</Text>
          <UIIcon
            src={privacyType === 'employee' ? PersonLine : TeamLine}
            size="small"
            className={atoms({color: 'Text Subtle', flexShrink: 0})}
          />
          <Text color="subtle">{name}</Text>
        </Box>
      );
    } catch {
      switch (value) {
        case 'WorkstreamPrivacy.PUBLIC':
          return (
            <>
              {t('workstream_audit_log_privacy_type', {
                type: t('workstream_settings_privacy_public'),
              })}
            </>
          );
        case 'WorkstreamPrivacy.PRIVATE':
          return (
            <>
              {t('workstream_audit_log_privacy_type', {
                type: t('workstream_settings_privacy_private'),
              })}
            </>
          );
        case 'WorkstreamPrivacy.CUSTOM':
          return (
            <>
              {t('workstream_audit_log_privacy_type', {
                type: t('workstream_settings_privacy_custom'),
              })}
            </>
          );
        default:
          return <>{value}</>;
      }
    }
  } else if (type === 'team') {
    try {
      const {name, slug} = JSON.parse(value);
      const label = name || slug;
      if (!label) {
        return <>{value}</>;
      }

      return (
        <Box display="flex" alignItems="center" style={{gap: 4}}>
          <UIIcon src={TeamLine} size="small" className={atoms({color: 'Text Subtle'})} />
          <Truncate tooltipControlProps={{placement: 'top'}} maxWidth={400}>
            <Text color="subtle">{label}</Text>
          </Truncate>
        </Box>
      );
    } catch {
      return <>{value}</>;
    }
  } else if (type === 'dependency') {
    const workstreamId = parseInt(value, 10);
    if (isNaN(workstreamId)) {
      return <>{value}</>;
    }
    return (
      <Box display="flex" alignItems="center" style={{gap: 2}}>
        <UIIcon src={WorkstreamsLine} size="small" className={atoms({color: 'Text Subtle'})} />
        <LoadableWorkstreamName workstreamId={workstreamId} />
      </Box>
    );
  } else if (type === 'metric_forecast') {
    try {
      const {title, period, target, budget, actual, forecast_date} = JSON.parse(value);
      const summaryParts = [
        getAuditTitleText(title),
        period
          ? `${t('workstream_audit_log_metric_forecast_period')}: ${formatMetricForecastPeriod(period)}`
          : null,
        target ? `${t('target')}: ${target}` : null,
        budget ? `${t('budget')}: ${budget}` : null,
        actual ? `${t('actual')}: ${actual}` : null,
        forecast_date
          ? `${t('workstream_audit_log_metric_forecast_date')}: ${formatMetricForecastDate(
              forecast_date
            )}`
          : null,
      ].filter(Boolean);

      return <>{summaryParts.join(' • ')}</>;
    } catch {
      return <>{value}</>;
    }
  } else if (type.startsWith('metric')) {
    try {
      const {
        type: metricType,
        title,
        current,
        target,
        owner_id,
        is_linked_metric,
      } = JSON.parse(value);

      if (type === 'metric_dri') {
        return (
          <Box display="flex">
            <Text color="subtle">{t('workstream_audit_log_metric_dri')}</Text>
            <Box flexShrink={0} marginLeft="8">
              <LoadableEmployeeName ownerId={owner_id} />
            </Box>
          </Box>
        );
      }

      return (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="flex-start">
            {is_linked_metric && (
              <UIIcon
                src={SyncingLine}
                size="small"
                className={atoms({color: 'Text Subtle'})}
                style={{marginTop: 3, marginRight: 2}}
              />
            )}
            <MetricIcon metricType={metricType} />

            <Text color="subtle" style={{maxWidth: '100%'}}>
              {node ? node : getAuditTitleText(title)}
            </Text>

            {target && current && (
              <Text
                style={{
                  marginLeft: 8,
                  flexShrink: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
                color="subtle"
              >
                {current}/{target}
              </Text>
            )}
          </Box>

          {owner_id && (
            <Box flexShrink={0} marginLeft="8">
              <LoadableEmployeeAvatar ownerId={owner_id} />
            </Box>
          )}
        </Box>
      );
    } catch {
      return <Text color="subtle">{value}</Text>;
    }
  } else if (type === 'employee') {
    const ownerId = parseInt(value, 10);
    if (isNaN(ownerId)) {
      return <>{value}</>;
    }
    return <LoadableEmployeeName ownerId={ownerId.toString()} />;
  } else if (['guiding_principles', 'obstacles_and_opportunities'].includes(type)) {
    return <>{parseAndExtractTextFromLexicalJson(value)}</>;
  }

  return <>{value}</>;
};

export const MetricIcon = ({metricType}: {metricType: string}) => {
  return (
    <UIIcon
      src={
        metricType === 'milestone'
          ? MilestoneLine
          : metricType === 'deliverable'
            ? PaperDocLine
            : MetricsLine
      }
      size="small"
      style={{marginTop: 3}}
      className={atoms({color: 'Text Subtle', flexShrink: 0, marginRight: '2'})}
    />
  );
};

export const ChangeLogLine = ({
  change,
  type,
  value,
  node,
  metrics,
  useFullWidth = false,
}: {
  change: 'add' | 'remove';
  type: string;
  value: string;
  node?: React.ReactNode;
  metrics?: WorkstreamMetric[];
  useFullWidth?: boolean;
}) => {
  const shouldUseFullWidth =
    useFullWidth || ['mnpi_metric_audience', 'mnpi_metric_toggle'].includes(type);

  return (
    <Box
      backgroundColor={change === 'add' ? 'Success Surface' : 'Alert Surface'}
      paddingX="8"
      borderRadius="XSmall"
    >
      <Box display="flex" alignItems="flex-start" style={{gap: 6}}>
        <UIIcon
          src={change === 'add' ? AddLine : MinusLine}
          size="small"
          className={atoms({color: 'Text Subtle', marginTop: '2', paddingTop: '2', flexShrink: 0})}
        />

        {shouldUseFullWidth ? (
          <Box style={{flexGrow: 1, minWidth: 0, maxWidth: CHANGE_LOG_CONTENT_MAX_WIDTH}}>
            <FormattedChangeLog type={type} value={value} node={node} metrics={metrics} />
          </Box>
        ) : (
          <Text
            color="faint"
            className={atoms({display: 'block'})}
            style={{maxWidth: CHANGE_LOG_CONTENT_MAX_WIDTH}}
          >
            <FormattedChangeLog type={type} value={value} node={node} metrics={metrics} />
          </Text>
        )}
      </Box>
    </Box>
  );
};

export const TeamAuditLog = ({slug}: {slug: string}) => {
  const {data, isLoading} = useQuery({
    queryKey: ['team', 'logs', slug],
    queryFn: () => getService(TeamService).getTeamLogsApiV1TeamsSlugLogsGet(slug),
  });

  const groupedLogs = groupByTimeAndType(data ?? []);

  if (isLoading) {
    return (
      <Box width="100%" display="flex" justifyContent="center" marginTop="68">
        <Spinner monochromatic />
      </Box>
    );
  }

  return (
    <>
      {!data?.length ? (
        <EmptyState
          title={t('audit_log_empty_title')}
          body={t('audit_log_empty_body')}
          image={<LaptopMini width="64" altText={''} />}
          hideBorder
        />
      ) : (
        Object.entries(groupedLogs)
          .sort(([a], [b]) => (new Date(a) > new Date(b) ? -1 : 1))
          .map(([timestamp, changesByType]) => {
            const changeTypes = Object.keys(changesByType);
            const firstChange = changesByType[changeTypes[0]][0];

            const creationType = changesByType['team'];

            return (
              <div key={timestamp}>
                <UpdatePost employee={firstChange.employee} update={{created_at: timestamp}}>
                  <Stack gap="8">
                    {!creationType &&
                      Object.entries(changesByType).map(([type, changes]) => (
                        <Stack key={`${timestamp}-${type}`} gap="2">
                          {changes.map(({before, after, date}) => (
                            <Stack key={`${type}-${before}-${after}—${date}`}>
                              {before && (
                                <ChangeLogLine change="remove" type={type} value={before} />
                              )}
                              {after && <ChangeLogLine change="add" type={type} value={after} />}
                            </Stack>
                          ))}
                        </Stack>
                      ))}
                    {creationType && (
                      <Text>
                        <Trans
                          i18nKey="audit_log_new_team"
                          t={t}
                          components={{b: <Text isBold />}}
                          values={{data: creationType[0].after}}
                        />
                      </Text>
                    )}
                  </Stack>
                </UpdatePost>
              </div>
            );
          })
      )}
    </>
  );
};

const LoadableEmployeeAvatar = ({ownerId}: {ownerId: string}) => {
  const {employee, isLoading} = useEmployee({ldap: ownerId});

  if (isLoading) {
    return <Skeleton.Avatar size="small" />;
  }

  if (!employee) {
    return null;
  }

  return (
    <Tooltip title={employee.name}>
      <Box>
        <Avatar user={employee} size="xsmall" />
      </Box>
    </Tooltip>
  );
};

const LoadableEmployeeName = ({ownerId}: {ownerId: string}) => {
  const {employee, isLoading} = useEmployee({ldap: ownerId});

  if (isLoading || !employee) {
    return null;
  }

  return <Text color="subtle">{employee.name}</Text>;
};

const LoadableAudienceEmployeeName = ({userId}: {userId: string}) => {
  const {employee, isLoading} = useEmployee({ldap: userId, throwOnError: false});

  if (isLoading) {
    return <Skeleton.Text width={96} size="small" />;
  }

  if (!employee) {
    return <Text color="subtle">{userId}</Text>;
  }

  return <Text color="subtle">{employee.name}</Text>;
};

const LoadableWorkstreamName = ({workstreamId}: {workstreamId: number}) => {
  const {workstream, isLoading} = useWorkstream(workstreamId);

  if (isLoading || !workstream) {
    return null;
  }

  return <Text color="subtle">{workstream.name}</Text>;
};
